// @ts-nocheck
/**
 * @fileoverview Analytics entry point for the theme.
 *
 * This module is the single entry point for ALL tracking services. It is
 * deliberately NOT GA4-specific: today `track()` pushes to `window.dataLayer`
 * (consumed by GTM → GA4), and additional destinations are added here later
 * without touching a single call site.
 *
 * Two responsibilities:
 *
 *   1. `track(eventName, details)` — the only way to emit an event. Every
 *      event name lives in the exported `ANALYTICS_EVENTS` constant, so
 *      renaming an event is a one-line change here rather than a
 *      codebase-wide search.
 *
 *   2. Page-level and delegated-listener tracking that has no natural source
 *      file — page views, collection views, add-to-cart, checkout clicks,
 *      navigation clicks, card clicks, recommendation impressions, banner and
 *      CTA clicks, expandable items, form starts and video engagement.
 *
 * Everything that DOES have a natural source file calls `track()` from there
 * instead, so the request/response shape has exactly one owner:
 *
 *   - hydrate-callers.js      → product search, store search, page template, redirect
 *   - query-autocomplete.js   → autocomplete
 *   - query-recommendations.js→ MLT recommendations
 *   - email-signup-form.js    → email lead (footer signup + exclusive-offer popup)
 *   - interstitial-redirect.js→ email lead + account registration (EZReg)
 *   - component-cart-items.js → remove from cart
 *
 * Loaded two ways, both resolving to this same module instance (so the
 * page-level init below runs exactly once):
 *   - `<script type="module">` in snippets/scripts.liquid — boots the page-level tracking
 *   - the `@theme/analytics` importmap alias — source files import `track`
 *
 * Purchase events cannot be tracked from theme JS (checkout and the Thank You
 * page do not run theme scripts) — see docs/analytics.md for the Customer
 * Events custom pixel that emits ng_purchase.
 *
 * @version 4.0.0
 */

import { decodeHtmlEntities } from '@theme/utilities';
import { getApiLocale } from '@theme/locale-map';
import { getApiRequestContext } from '@theme/api-request-context';

/**
 * Every event name the theme emits. Exported so call sites never hard-code a
 * string — renaming an event means editing this object only.
 *
 * Values are the literal dataLayer event names and are contract with GTM;
 * changing one requires a matching GTM trigger update.
 */
export const ANALYTICS_EVENTS = Object.freeze({
  PRODUCT_SEARCH: 'ng_product_search',
  STORE_SEARCH: 'ng_store_search',
  PAGE_TEMPLATE: 'ng_page_template',
  SEARCH_REDIRECT: 'ng_search_redirect',
  AUTOCOMPLETE: 'ng_autocomplete',
  AUTOCOMPLETE_TERM_CLICK: 'ng_autocomplete_term_click',
  AUTOCOMPLETE_PRODUCT_CLICK: 'ng_autocomplete_product_click',
  RECOMMENDATIONS: 'ng_recommendations',
  RECOMMENDATION_IMPRESSION: 'ng_recommendation_impression',
  PRODUCT_CLICK: 'ng_product_click',
  STORE_CLICK: 'ng_store_click',
  STORE_PAGE_VIEW: 'ng_store_page_view',
  STORE_LINK_OFF: 'ng_store_link_off',
  NAVIGATION_CLICK: 'ng_navigation_click',
  COLLECTION_VIEW: 'ng_collection_view',
  PAGE_VIEW: 'ng_page_view',
  ADD_TO_CART: 'ng_add_to_cart',
  REMOVE_FROM_CART: 'ng_remove_from_cart',
  CHECKOUT_INITIATED: 'ng_checkout_initiated',
  EMAIL_LEAD: 'ng_email_lead',
  ACCOUNT_REGISTRATION: 'ng_account_registration',
  FORM_START: 'ng_form_start',
  FORM_SUBMIT: 'ng_form_submit',
  CTA_CLICK: 'ng_cta_click',
  BANNER_CLICK: 'ng_banner_click',
  EXPANDABLE_CLICK: 'ng_expandable_click',
  VIDEO_START: 'ng_video_start',
  VIDEO_PROGRESS: 'ng_video_progress',
  VIDEO_COMPLETE: 'ng_video_complete',
  // GA4's own ecommerce event name, deliberately without the `ng_` prefix: GA4
  // only recognises this list-click as `select_item`, and renaming it would
  // make it an ordinary custom event again.
  SELECT_ITEM: 'select_item',
});

/** @type {Object|null} Cached autocomplete request, for click attribution. */
let _lastAutocompleteRequest = null;

/**
 * Response-derived keys on the autocomplete event (see `trackAutocomplete` in
 * query-autocomplete.js). Excluded from the click-attribution cache so the
 * suggestion-click events carry request context only.
 */
const AUTOCOMPLETE_RESPONSE_KEYS = new Set([
  'searchTerm',
  'termsCount',
  'productsCount',
  'responseOk',
  'errorMessage',
  'products',
]);

/* ==================================================================
 * 1. PAGE CONTEXT
 * ================================================================*/

/**
 * Reads the server-side Liquid context set by analytics-context.liquid,
 * enriched with client-side globals (Theme, customer meta tag).
 *
 * Deliberately narrow: page_location, page_title and page_referrer are
 * collected by GA4 automatically, so they are not duplicated here.
 *
 * @returns {Object} Context used to stamp every event.
 */
/**
 * Reads a cookie by name. Returns '' when absent or unreadable.
 * @param {string} name
 * @returns {string}
 */
function readCookie(name) {
  try {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  } catch {
    return '';
  }
}

/**
 * Shopify's own visitor and session identifiers, for joining GA4 back to
 * Shopify-side data (MA-2968).
 *
 * `_shopify_y` is the **visitor** token — a UUID that persists about a year per
 * device — and is the identifier actually available on this storefront. It is
 * also readable through `ShopifyAnalytics.lib.user().traits().uniqToken`, which
 * is the fallback here for the case where the cookie is host-scoped differently
 * than the page.
 *
 * `_shopify_s` is the **session** token, and Shopify only issues it once the
 * visitor has granted analytics consent. Where consent has not been collected
 * it is simply absent, and this reports an empty string rather than inventing a
 * substitute: a theme-generated id would join to nothing on the Shopify side,
 * which is the whole point of asking for this one. See docs/analytics.md.
 *
 * @returns {{ visitorId: string, sessionId: string }}
 */
/**
 * GA4's own client id, from the `_ga` cookie (`GA1.1.<id>.<ts>` — the id is the
 * last two segments). GA4 knows this about itself, but it is not in the
 * dataLayer unless the theme puts it there, and a report that joins GA4 to
 * anything else needs it alongside MA's own identifiers rather than inside
 * Google's own storage.
 *
 * @returns {string}
 */
function getGoogleClientId() {
  const raw = readCookie('_ga');
  if (!raw) return '';
  const parts = raw.split('.');
  return parts.length >= 4 ? parts.slice(-2).join('.') : '';
}

function getShopifyIds() {
  let uniqToken = '';
  try {
    uniqToken = window.ShopifyAnalytics?.lib?.user?.()?.traits?.()?.uniqToken || '';
  } catch {
    // Shopify's analytics library is absent or blocked — the cookie still may not be.
  }

  return {
    visitorId: readCookie('_shopify_y') || uniqToken,
    sessionId: readCookie('_shopify_s'),
  };
}

function getPageContext() {
  const serverCtx = window.__analyticsContext || {};
  const meta = document.querySelector('meta[name="ma:customer"]');
  const shopify = getShopifyIds();
  const { languageCode, countryCode } = getApiLocale();
  const { siteType } = getApiRequestContext(countryCode);

  return {
    shopifyVisitorId: shopify.visitorId,
    shopifySessionId: shopify.sessionId,
    pageType: serverCtx.pageType || serverCtx.page?.type || '',
    // `pageType` is Shopify's `template.name`, so every `page.*` template
    // reports the bare string "page". These two disambiguate it: `pageName` is
    // the resource handle (store-detail, careers, join…) and `pageTemplate` the
    // full template string including its suffix.
    pageName: serverCtx.pageName || '',
    pageTemplate: serverCtx.page?.template || '',
    customerId: meta?.dataset?.customerId || '',
    creditUser: meta?.dataset?.creditUser || '',
    pcId: meta?.dataset?.pcId || '',
    sponsorId: meta?.dataset?.sponsorId || '',
    shopperCid: meta?.dataset?.shopperCid || '',
    cartId: meta?.dataset?.cartId || '',
    googleClientId: getGoogleClientId(),
    isPpc: meta?.dataset?.isPpc || '',
    portalId: window.customerPortalId || '',
    // The same locale and site-type the NG API calls are made with, so a report
    // can split any event by market without joining back to a search event.
    // Taken from the shared helpers rather than re-derived, so the two can
    // never disagree about which market a session is in.
    siteType,
    languageCode,
    countryCode,
  };
}

/* ==================================================================
 * 2. THE TRACK DISPATCHER
 * ================================================================*/

/**
 * Emits an analytics event. The single entry point for all tracking.
 *
 * Currently pushes to `window.dataLayer` for GTM; additional destinations
 * (server-side collectors, other vendors) get added here, not at call sites.
 *
 * No-ops when the Liquid context snippet did not render, which is how the
 * theme expresses "GTM is not configured for this store".
 *
 * @param {string} eventName - A value from `ANALYTICS_EVENTS`.
 * @param {Object} [details] - Event-specific parameters.
 */
export function track(eventName, details = {}) {
  if (!window.__analyticsContext) return;

  const ctx = getPageContext();
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    // A logged-out shopper carries no user id at all. This used to report the
    // literal string 'anonymous', which GA4 accepted as a real user id — so
    // every logged-out visitor on the site collapsed into one shared identity.
    // `customerId` is the same value under the name the analytics spec asks
    // for; `userId` stays because existing GTM variables are mapped to it.
    ...(ctx.customerId ? { userId: ctx.customerId, customerId: ctx.customerId } : {}),
    creditUserId: ctx.creditUser,
    pcId: ctx.pcId,
    sponsorId: ctx.sponsorId,
    shopperCid: ctx.shopperCid,
    cartId: ctx.cartId,
    googleClientId: ctx.googleClientId,
    isPpc: ctx.isPpc,
    portalId: ctx.portalId,
    siteType: ctx.siteType,
    languageCode: ctx.languageCode,
    countryCode: ctx.countryCode,
    pageType: ctx.pageType,
    pageName: ctx.pageName,
    pageTemplate: ctx.pageTemplate,
    shopifyVisitorId: ctx.shopifyVisitorId,
    shopifySessionId: ctx.shopifySessionId,
    ...details,
  });

  // Side-effects that keep cross-event state inside this module, so source
  // files only ever have to call track().
  if (eventName === ANALYTICS_EVENTS.PRODUCT_SEARCH) {
    storeLastSearch(details.query || '', details.searchContext || '');
  } else if (eventName === ANALYTICS_EVENTS.AUTOCOMPLETE) {
    // Cache the REQUEST half only. The autocomplete event also carries response
    // counts and flattened messages_*/metadata_* keys, and the click handlers
    // below spread this cache into their own events — without the filter the
    // suggestion-click events would inherit a response payload describing a
    // different interaction.
    _lastAutocompleteRequest = Object.fromEntries(
      Object.entries(details).filter(
        ([key]) => !AUTOCOMPLETE_RESPONSE_KEYS.has(key) && !key.startsWith('messages_') && !key.startsWith('metadata_')
      )
    );
  } else if (eventName === ANALYTICS_EVENTS.RECOMMENDATIONS) {
    // Recommendation cards inject after the API resolves — rescan for
    // impressions once they exist.
    setTimeout(trackRecommendationImpressions, 0);
  }
}

/* ==================================================================
 * 3. LAST-SEARCH CONTEXT (click attribution)
 * ================================================================*/

/**
 * Stores the last search context so downstream card/cart events can be
 * attributed back to the search that produced them.
 * @param {string} query
 * @param {string} context
 */
function storeLastSearch(query, context) {
  try {
    sessionStorage.setItem('ma_last_search', JSON.stringify({ query, context, timestamp: Date.now() }));
  } catch {
    // sessionStorage may be full or unavailable
  }
}

/**
 * Reads the last search context.
 *
 * Exported so the files that own an API call can attribute their own events
 * back to the search that produced them, the same way the click handlers here
 * do — without each one re-reading sessionStorage under its own key.
 *
 * @returns {{ query: string, context: string, timestamp: number } | null}
 */
export function getLastSearch() {
  try {
    const raw = sessionStorage.getItem('ma_last_search');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/* ==================================================================
 * 4. RAW RESPONSE HELPERS
 * ================================================================*/

/**
 * Flattens `messages` and `metadata` from a raw NG API response into
 * underscore-joined keys (messages_spellCheck_originalQuery, metadata_traceId…).
 *
 * Exported because the source files that own each API call build their own
 * event payloads and need the same flattening.
 *
 * @param {Object|null} raw - The raw API JSON response.
 * @returns {Object} Flat object of messages_* / metadata_* keys.
 */
export function flattenRawMeta(raw) {
  if (!raw) return {};
  const result = {};

  /**
   * Recursively flattens nested objects into underscore-joined keys.
   * Arrays and primitives are kept as-is; only plain objects are descended.
   * @param {Object} obj    - Object to flatten.
   * @param {string} prefix - Current key prefix (e.g. "messages").
   */
  function flatten(obj, prefix) {
    for (const [key, value] of Object.entries(obj)) {
      const flatKey = prefix ? prefix + '_' + key : key;
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        flatten(value, flatKey);
      } else {
        result[flatKey] = value;
      }
    }
  }

  if (raw.messages && typeof raw.messages === 'object') flatten(raw.messages, 'messages');
  if (raw.metadata && typeof raw.metadata === 'object') flatten(raw.metadata, 'metadata');

  return result;
}


/**
 * Request keys deliberately dropped from every event.
 *
 * `fields` and `aggregations` are static request scaffolding — identical on
 * every search, so they carry no analytical signal — and both are long arrays
 * that would blow past GA4's 100-character parameter limit the moment anyone
 * mapped them. `signal` is an AbortSignal and is not data at all.
 */
const REQUEST_KEYS_OMITTED = new Set(['fields', 'aggregations', 'signal', 'extra']);

/**
 * Normalises an NG request body for the dataLayer.
 *
 * Nested objects are flattened the same way `flattenRawMeta` treats the
 * response — `distributorInfo.isDistributor` becomes
 * `distributorInfo_isDistributor` — so every value is a scalar a GTM variable
 * can point at directly, and the NG field path stays readable. Without this a
 * variable aimed at `distributorInfo` itself resolves to `[object Object]` in
 * GA4.
 *
 * `filters` is the exception and stays a nested object: its keys are facet ids
 * that vary per search, so flattening it would produce unpredictable parameter
 * names — the very problem flattening is meant to solve. Two flat companions
 * are added instead, which is what a report can actually group by.
 *
 * Arrays of real data (`akeneoProductIds`, `moduleOrder`) are left alone; GTM
 * joins them if it wants them.
 *
 * @param {Object|null|undefined} request - The exact body sent to the API.
 * @returns {Object} Flat request params, plus `filters` / `filtersApplied` / `filtersCount`.
 */
export function flattenRequest(request) {
  /** @type {Record<string, any>} */
  const result = {};
  if (!request || typeof request !== 'object') return result;

  /**
   * @param {Object} obj
   * @param {string} prefix
   */
  function flatten(obj, prefix) {
    for (const [key, value] of Object.entries(obj)) {
      const flatKey = prefix ? prefix + '_' + key : key;
      if (!prefix && REQUEST_KEYS_OMITTED.has(key)) continue;

      if (!prefix && key === 'filters') {
        const entries = Object.entries(value || {});
        result.filters = value || {};
        result.filtersCount = entries.length;
        // `node.id:123|456,isOnSale:true` — one readable value a report can
        // group by, capped to GA4's parameter limit.
        result.filtersApplied = entries
          .map(([name, val]) => name + ':' + [].concat(val ?? []).join('|'))
          .join(',')
          .slice(0, 100);
        continue;
      }

      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        flatten(value, flatKey);
      } else {
        result[flatKey] = value;
      }
    }
  }

  flatten(request, '');
  return result;
}

/* ==================================================================
 * 5. PRODUCT CLICK TRACKING
 * ================================================================*/

/**
 * Standard per-product analytics payload, built from an NG API product hit
 * (search, recommendations, autocomplete). Field names mirror the API
 * verbatim so GTM mappings match the API documentation.
 *
 * @param {Object} hit - NG product hit.
 * @returns {{ prodId: string, skus: string[], catalogSku: string, akeneoProductId: string, shopifyProductId: string, shopifyProductHandle: string, shopifyVariants: Array<{ merchantSku: string, shopifyVariantId: string }> }}
 */
export function buildProductAnalyticsData(hit) {
  const product = hit || {};
  const catalogSku = product.catalogSku ?? '';
  // A real id always wins over a derived one: the API when it ever returns one,
  // then the Shopify metafield, which the Liquid cards pass in here verbatim.
  const akeneoFromSource = product.akeneoProductId ?? product.supplemental?.goldenRecord?.akeneoProductId ?? '';
  // The same market the request was made for, so a composed id can never claim a
  // different market from the search that returned the product. Both halves are
  // required: an id missing its suffix would be wrong rather than incomplete.
  const market = (getApiLocale().countryCode || '').toLowerCase();
  return {
    prodId: product.prodId ?? '',
    skus: Array.isArray(product.skus) ? product.skus : [],
    catalogSku,
    // The Akeneo id is the catalogSku with the market appended in lower case
    // (5826599 -> 5826599twn), confirmed against the PIM on both the US and
    // Taiwan catalogues. The search response carries no Akeneo field of its own,
    // so for an API-rendered card the id is composed from the catalogSku and the
    // market the request was made for — the same `countryCode` sent to the API,
    // so the two can never disagree about which market a product was seen in.
    // Composed only as a last resort: a real id from the API or the Shopify
    // metafield is used as-is whenever one exists, and nothing is composed when
    // there is no catalogSku to compose from.
    akeneoProductId: akeneoFromSource || (catalogSku && market ? catalogSku + market : ''),
    shopifyProductId: product.shopifyProductId ?? '',
    shopifyProductHandle: product.shopifyProductHandle ?? '',
    shopifyVariants: (Array.isArray(product.shopifyVariants) ? product.shopifyVariants : []).map((variant) => ({
      merchantSku: variant?.merchantSku ?? '',
      shopifyVariantId: variant?.shopifyVariantId ?? '',
    })),
  };
}

/**
 * Maps a list of NG product hits to analytics payloads.
 * @param {Object[]|null|undefined} hits
 * @returns {ReturnType<typeof buildProductAnalyticsData>[]}
 */
export function buildProductsAnalyticsData(hits) {
  if (!Array.isArray(hits)) return [];
  // 1-based position within this response, so a report can tell a first-row
  // click from a last-row one. It restarts at 1 on every page of a paginated
  // list, because it describes the response rather than the whole result set.
  return hits.map((hit, index) => ({ ...buildProductAnalyticsData(hit), position: index + 1 }));
}

/**
 * Products seen in API responses, keyed by prodId, Shopify product id and
 * handle, so a later click on the rendered card can carry the full payload.
 * @type {Map<string, ReturnType<typeof buildProductAnalyticsData>>}
 */
const _analyticsProducts = new Map();

/**
 * Name, brand and price for the GA4 `select_item` event, kept apart from the
 * payload above so that one stays exactly the shape the API documents.
 *
 * @param {Object} hit - NG product hit.
 */
function buildItemMeta(hit) {
  const h = hit || {};
  const price = h.product?.priceInfo?.minOnSalePrice ?? h.product?.priceInfo?.minRegularPrice ?? null;
  return {
    // The API returns these HTML-encoded (`Isotonix&reg;`), and GA4 would
    // report the entity verbatim.
    title: decodeHtmlEntities(h.derived?.title || h.product?.caption || ''),
    brand: decodeHtmlEntities(h.supplemental?.taxonomy?.brand?.name || h.storeName || ''),
    price,
  };
}

/** @type {Map<string, ReturnType<typeof buildItemMeta>>} */
const _analyticsItemMeta = new Map();

/**
 * Registers API product hits for click attribution.
 * @param {Object[]|null|undefined} hits
 */
export function registerAnalyticsProducts(hits) {
  if (!Array.isArray(hits)) return;
  for (const hit of hits) {
    const payload = buildProductAnalyticsData(hit);
    const meta = buildItemMeta(hit);
    for (const key of [payload.prodId, payload.shopifyProductId, payload.shopifyProductHandle]) {
      if (key !== '' && key != null) {
        _analyticsProducts.set(String(key), payload);
        _analyticsItemMeta.set(String(key), meta);
      }
    }
  }
}

/**
 * Looks up a registered product payload by prodId, Shopify product id or handle.
 * @param {string|number|null|undefined} key
 * @returns {ReturnType<typeof buildProductAnalyticsData>|null}
 */
export function getAnalyticsProduct(key) {
  if (key === '' || key == null) return null;
  return _analyticsProducts.get(String(key)) || null;
}

/**
 * Card containers that identify a product across every surface:
 *   - `[data-ng-prod-id]`  — NG client-rendered cards (ng-product-card.js)
 *   - `[data-product-id]`  — Liquid cards (product-card-compact, cart recs, …)
 *   - `[data-akeneo-id]`   — cart/account recommendation cards
 */
const PRODUCT_CARD_SELECTOR = '[data-ng-prod-id], [data-product-id], [data-akeneo-id]';

/**
 * Resolves the human-meaningful list context for a card: the enclosing
 * Shopify section id, recommendation custom element, or 'unknown'.
 * @param {Element} card
 * @returns {string}
 */
function resolveListName(card) {
  const section = card.closest(
    'custom-product-recommendations-api, product-recommendations, cart-drawer-recommendations, account-recommendations, [data-section-id], [id^="shopify-section-"], [id^="cart-recommended-products-"]'
  );
  if (!section) return 'unknown';
  return section.dataset?.sectionId || section.id || section.tagName.toLowerCase();
}

/**
 * Returns the 1-based position of `el` among siblings matching `selector`.
 * @param {Element} el
 * @param {string} selector
 * @param {Element|null} [container] - Defaults to the element's parent.
 * @returns {number} 1-based index, or 0 when not found.
 */
function resolvePosition(el, selector, container) {
  const parent = container || el.parentElement;
  if (!parent) return 0;
  const siblings = parent.querySelectorAll(selector);
  for (let i = 0; i < siblings.length; i++) {
    if (siblings[i] === el) return i + 1;
  }
  return 0;
}

/**
 * Delegated click handler for product cards across every surface.
 */
function trackProductClicks() {
  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;
    const card = e.target.closest(PRODUCT_CARD_SELECTOR);
    if (!card) return;

    // Skip store cards and autocomplete product rows (handled separately).
    if (card.closest('.ng-store-card, [data-volume-id]')) return;
    if (card.closest('[data-kind="product"]')) return;

    // Recommendation section roots carry the SOURCE product's id (e.g.
    // <custom-product-recommendations-api data-product-id>) — only clicks
    // that resolve to an actual card inside them count.
    if (
      card.matches(
        'custom-product-recommendations-api, product-recommendations, cart-drawer-recommendations, account-recommendations'
      )
    ) {
      return;
    }

    // Determine clickType: add-to-cart button vs subscription vs product detail.
    // NG cards use data-ng-add / data-ng-subscribe; Liquid PDP forms use
    // [name="add"]; keep the legacy selectors as a safety net.
    let clickType = 'product_detail';
    const clickedEl = e.target.closest('button, a, [role="button"]');
    if (clickedEl) {
      if (clickedEl.closest('[data-ng-subscribe], [data-subscription], .subscription-option')) {
        clickType = 'subscription';
      } else if (clickedEl.closest('[data-ng-add], [data-add-to-cart], .add-to-cart, [name="add"]')) {
        clickType = 'add_to_cart';
      }
    }

    const productHandle = card.dataset.ngHandle || card.dataset.productHandle || '';
    // Cards rendered from Shopify rather than from a search response are not in
    // the registry and have no catalogSku — the spec's fallback is the Akeneo id
    // from the product metafield, which the Liquid card renders as
    // `data-akeneo-id`. Built through the same helper so the payload is one
    // shape everywhere, with whichever id that surface actually has.
    const product =
      getAnalyticsProduct(card.dataset.ngProdId || card.dataset.akeneoId) ||
      getAnalyticsProduct(productHandle) ||
      (card.dataset.akeneoId
        ? buildProductAnalyticsData({
            akeneoProductId: card.dataset.akeneoId,
            shopifyProductId: card.dataset.productId,
            shopifyProductHandle: productHandle,
          })
        : null);

    track(ANALYTICS_EVENTS.PRODUCT_CLICK, {
      clickedProductId: card.dataset.ngProdId || card.dataset.productId || card.dataset.akeneoId || '',
      clickPosition: resolvePosition(card, PRODUCT_CARD_SELECTOR),
      clickType: clickType,
      productHandle: productHandle,
      listName: resolveListName(card),
      analyticsId: resolveAnalyticsId(card),
      lastSearch: getLastSearch(),
      ...(product ? { product } : {}),
    });

    // GA4's native list-click event, emitted alongside `ng_product_click`
    // rather than instead of it: the custom event carries MA's own identifiers,
    // this one is what GA4's built-in ecommerce reports read. Only a product
    // detail click is a `select_item` — adding to cart is `add_to_cart`.
    if (clickType === 'product_detail') {
      const listName = resolveListName(card);
      const analyticsId = resolveAnalyticsId(card);
      const meta = _analyticsItemMeta.get(String(card.dataset.ngProdId || card.dataset.akeneoId || productHandle)) || null;
      window.dataLayer = window.dataLayer || [];
      // GA4 requires the previous ecommerce object to be cleared, or the next
      // event inherits whatever items the last one carried.
      window.dataLayer.push({ ecommerce: null });
      track(ANALYTICS_EVENTS.SELECT_ITEM, {
        ecommerce: {
          item_list_id: analyticsId || listName,
          item_list_name: listName,
          items: [
            {
              item_id:
                product?.catalogSku ||
                product?.akeneoProductId ||
                card.dataset.ngProdId ||
                card.dataset.productId ||
                card.dataset.akeneoId ||
                '',
              ...(meta?.title ? { item_name: meta.title } : {}),
              ...(meta?.brand ? { item_brand: meta.brand } : {}),
              ...(meta?.price != null ? { price: meta.price } : {}),
              index: resolvePosition(card, PRODUCT_CARD_SELECTOR),
              item_list_id: analyticsId || listName,
              item_list_name: listName,
              quantity: 1,
            },
          ],
        },
      });
    }
  });
}

/* ==================================================================
 * 6. STORE CLICK TRACKING
 * ================================================================*/

/**
 * Delegated click handler for NG store cards — rendered by ng-store-card.js
 * as `<li class="ng-store-card" data-volume-id="…" data-store-name="…">`.
 */
function trackStoreClicks() {
  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;
    const card = e.target.closest('.ng-store-card, [data-volume-id]');
    if (!card) return;

    track(ANALYTICS_EVENTS.STORE_CLICK, {
      clickedVolumeId: card.dataset.volumeId || '',
      storeName: card.dataset.storeName || '',
      clickPosition: resolvePosition(card, '.ng-store-card, [data-volume-id]'),
      analyticsId: resolveAnalyticsId(card),
      lastSearch: getLastSearch(),
    });
  });
}

/* ==================================================================
 * 6b. STORE LINK-OFF TRACKING
 * ================================================================*/

/**
 * Anchors that are a partner hand-off in their own right, independent of the
 * href: the store-detail "Visit Store" button, and anything a template opts in
 * with `data-store-link-off`.
 */
const STORE_LINK_OFF_SELECTOR = '.hydrate-store-detail__external-link, [data-store-link-off]';

/** Volume id embedded in a shop.com link-off path (`…-vid301946-xservice-link_off.xhtml`). */
const VOLUME_ID_IN_URL = /[-/]vid(\d+)/i;

/**
 * The volume id of the store page currently being viewed. Store pages are
 * `/pages/store-detail?volumeId=…`, so the id lives in the query string — this
 * is the fallback when a link-off anchor sits outside any store card.
 *
 * @returns {string}
 */
function currentPageVolumeId() {
  try {
    return new URLSearchParams(window.location.search).get('volumeId') || '';
  } catch {
    return '';
  }
}

/**
 * Delegated click handler for the moment a shopper leaves us for a partner
 * store's own site.
 *
 * Two conditions, both required. The anchor must actually leave this origin —
 * a store card pointing at `/pages/store-detail?volumeId=…` is internal
 * navigation and is already covered by `ng_store_click`. And it must be a
 * store hand-off rather than any outbound link: the store-detail external
 * link, a shop.com `link_off` path, or an anchor inside a store card.
 *
 * A store card that links straight off-site therefore emits both
 * `ng_store_click` and `ng_store_link_off`. That is deliberate — they answer
 * different questions (which store was chosen, and that the shopper left).
 */
function trackStoreLinkOff() {
  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;
    const link = e.target.closest('a[href]');
    if (!link) return;

    const leavesOrigin = !!link.host && link.host !== window.location.host;
    if (!leavesOrigin) return;

    const card = link.closest('.ng-store-card, [data-volume-id]');
    const isStoreHandoff =
      link.matches(STORE_LINK_OFF_SELECTOR) || /link_off/i.test(link.getAttribute('href') || '') || !!card;
    if (!isStoreHandoff) return;

    const idFromUrl = VOLUME_ID_IN_URL.exec(link.href);

    track(ANALYTICS_EVENTS.STORE_LINK_OFF, {
      linkOffUrl: link.href || '',
      volumeId: card?.dataset?.volumeId || idFromUrl?.[1] || currentPageVolumeId(),
      storeName: card?.dataset?.storeName || '',
      destinationHost: link.host || '',
      linkText: link.textContent?.trim() || '',
      analyticsId: resolveAnalyticsId(link),
      lastSearch: getLastSearch(),
    });
  });
}

/* ==================================================================
 * 7. AUTOCOMPLETE CLICK TRACKING
 * ================================================================*/

/**
 * Delegated click handler for header-search suggestion rows
 * (assets/header-search.js):
 *   - term rows:    `li[data-kind="term"]` with `data-term`
 *   - recent rows:  `li[data-kind="recent"]` with `data-term`
 *   - product rows: `li[data-kind="product"]` with `data-product-id`
 */
function trackAutocompleteClicks() {
  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;

    // Term + recent-search suggestion rows.
    const termRow = e.target.closest('li[data-kind="term"], li[data-kind="recent"]');
    if (termRow) {
      // Skip the delete-recent button — removing a term is not a search click.
      if (e.target.closest('[data-delete-recent]')) return;

      const container = termRow.closest('ul, ol');
      track(ANALYTICS_EVENTS.AUTOCOMPLETE_TERM_CLICK, {
        clickedTerm: termRow.dataset.term || termRow.textContent?.trim() || '',
        clickPosition: resolvePosition(termRow, `li[data-kind="${termRow.dataset.kind}"]`, container),
        suggestionType: termRow.dataset.kind,
        analyticsId: resolveAnalyticsId(termRow),
        ...(_lastAutocompleteRequest || {}),
      });
      return;
    }

    // Product suggestion rows.
    const productRow = e.target.closest('li[data-kind="product"]');
    if (productRow) {
      const container = productRow.closest('ul, ol');
      const product = getAnalyticsProduct(productRow.dataset.productId);
      track(ANALYTICS_EVENTS.AUTOCOMPLETE_PRODUCT_CLICK, {
        clickedProductId: productRow.dataset.productId || '',
        clickPosition: resolvePosition(productRow, 'li[data-kind="product"]', container),
        analyticsId: resolveAnalyticsId(productRow),
        ...(_lastAutocompleteRequest || {}),
        ...(product ? { product } : {}),
      });
    }
  });
}

/* ==================================================================
 * 8. NAVIGATION CLICK TRACKING
 * ================================================================*/

/**
 * 1-based position of a nav link among the links it is actually grouped with.
 *
 * Deliberately makes no assumption about the markup. An earlier version keyed
 * off `closest('ul, ol, nav')` and reported 0 for the entire mega-menu, whose
 * columns are `<div>`s — the header's `<ul>` menus worked and the dropdowns
 * silently did not.
 *
 * Instead, climb to the nearest ancestor that groups more than one link and
 * number within it. That lands on the right container in every shape the theme
 * uses: a `<ul>` menu, a mega-menu column `<div>`, or a bare row of anchors.
 * It also keeps nested submenus honest — for `<li><a>Top</a><ul>…</ul></li>`
 * the climb stops at the `<li>`, so `Top` is first among its own group and its
 * children are numbered within their submenu, rather than every child pushing
 * the next top-level item further down.
 *
 * @param {Element} link
 * @returns {number} 1-based index, or 0 if the link isn't found in its group.
 */
function resolveNavPosition(link) {
  let group = null;
  for (let node = link.parentElement; node && node !== document.body; node = node.parentElement) {
    if (node.querySelectorAll('a[href]').length > 1) {
      group = node;
      break;
    }
  }

  // A link that shares its container with nothing else is the first of one.
  if (!group) return 1;

  const links = group.querySelectorAll('a[href]');
  for (let i = 0; i < links.length; i++) {
    if (links[i] === link) return i + 1;
  }
  return 0;
}

/**
 * Delegated click handler for header / footer navigation links.
 */
function trackNavigationClicks() {
  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;

    const headerLink = e.target.closest('#header-group a[href], .header a[href]');
    if (headerLink) {
      // Skip logo / cart / account icon clicks — track only menu links.
      if (headerLink.closest(PRODUCT_CARD_SELECTOR)) return;
      if (headerLink.closest('.cart-bubble, [data-cart-count], li[data-kind]')) return;

      track(ANALYTICS_EVENTS.NAVIGATION_CLICK, {
        linkText: headerLink.textContent?.trim() || '',
        linkUrl: headerLink.href || '',
        linkPosition: resolveNavPosition(headerLink),
        navSection: 'header',
        analyticsId: resolveAnalyticsId(headerLink),
      });
      return;
    }

    const footerLink = e.target.closest('footer a[href], #footer-group a[href]');
    if (footerLink) {
      track(ANALYTICS_EVENTS.NAVIGATION_CLICK, {
        linkText: footerLink.textContent?.trim() || '',
        linkUrl: footerLink.href || '',
        linkPosition: resolveNavPosition(footerLink),
        navSection: 'footer',
        analyticsId: resolveAnalyticsId(footerLink),
      });
    }
  });
}

/* ==================================================================
 * 9. COLLECTION VIEW
 * ================================================================*/

/**
 * Fires a collection view event when the page is a collection template
 * and server-side context includes collection data.
 */
function trackCollectionView() {
  const ctx = window.__analyticsContext || {};
  if (ctx.page?.type !== 'collection' || !ctx.collection) return;

  track(ANALYTICS_EVENTS.COLLECTION_VIEW, {
    collectionName: ctx.collection.title || '',
    collectionHandle: ctx.collection.handle || '',
    collectionId: ctx.collection.id,
    productsCount: ctx.collection.products_count,
    filterNodeId: ctx.collection.filterNodeId || '',
    searchTerm: ctx.collection.searchTerm || '',
  });
}

/* ==================================================================
 * 10. PAGE VIEW
 * ================================================================*/

/**
 * Fires a page view on every load, enriched with resource-specific data.
 *
 * page_location / page_title / page_referrer are NOT included: GA4 collects
 * them automatically on every event.
 */
function trackPageView() {
  const ctx = window.__analyticsContext || {};
  const pageType = ctx.pageType || ctx.page?.type || '';
  const eventParams = { pageType };

  if (ctx.page?.type === 'product' && ctx.product) {
    eventParams.productId = ctx.product.id;
    eventParams.productTitle = ctx.product.title;
    eventParams.productHandle = ctx.product.handle;
    eventParams.productVendor = ctx.product.vendor;
    eventParams.productType = ctx.product.type;
    eventParams.productPrice = ctx.product.price;
    eventParams.productSku = ctx.product.sku;
    eventParams.productAvailable = ctx.product.available;
    eventParams.akeneoProductId = ctx.product.akeneoProductId || '';
  }

  // Cart pages report their totals here; there is no separate cart-view event.
  if (ctx.page?.type === 'cart' && ctx.cart) {
    eventParams.cartItemCount = ctx.cart.item_count;
    eventParams.cartTotal = ctx.cart.total_price;
  }

  if (ctx.page?.type === 'search' && ctx.search) {
    // Shopify's `search.terms` comes back HTML-escaped, so a query containing
    // an ampersand arrives as "energy &amp; endurance supplements" and would be
    // reported that way in GA4. `ng_product_search.query` — taken from the API
    // request rather than Liquid — is already correct, so decoding here is what
    // keeps the two agreeing on the same search.
    eventParams.searchTerms = decodeHtmlEntities(ctx.search.terms || '');
    eventParams.nativeResultsCount = ctx.search.results_count;
  }

  if (ctx.page?.type === 'article' && ctx.article) {
    eventParams.articleId = ctx.article.id;
    eventParams.articleTitle = ctx.article.title;
    eventParams.articleAuthor = ctx.article.author;
  }

  track(ANALYTICS_EVENTS.PAGE_VIEW, eventParams);
}

/* ==================================================================
 * 11. ADD TO CART
 * ================================================================*/

/**
 * `detail.data.source` values of CartAddEvent dispatchers (assets/events.js
 * — the event name is shared with CartUpdateEvent as 'cart:update', so the
 * source is the only way to tell adds apart from quantity syncs).
 */
const CART_ADD_SOURCES = new Set([
  'product-form-component', // PDP / quick-add form (product-form.js)
  'ng-product-card', // NG search/recommendation card "+ Add"
  'custom-product-card', // Liquid custom product card
  'saved-for-later', // save-for-later re-add
  'subscription-drawer', // subscription add
  'quick-order-quantity', // quick order list steppers
]);

/**
 * Listens for the theme's cart events. Every add-to-cart path in the theme
 * dispatches the same bubbling CartAddEvent, so one listener here covers all
 * six sources — this is a shared event bus, not duplicated per-source logic.
 */
function trackAddToCart() {
  document.addEventListener('cart:update', function (e) {
    const detail = e.detail || {};
    const data = detail.data || {};
    if (data.didError) return;
    if (!CART_ADD_SOURCES.has(data.source)) return;

    if (data.source === 'subscription-drawer' && !data.variantId) return;

    const lastSearch = getLastSearch();
    // Most add paths dispatch on `document`, so fall back to the element that
    // still holds focus after the click (the add button) to find its section.
    const origin = e.target instanceof Element ? e.target : document.activeElement;
    // Same per-product payload the click events carry, so `catalogSku` is on
    // every event that has product data rather than only on the click. Three
    // sources, because an add can start from three kinds of surface: a card the
    // search API rendered, a Liquid card, or the PDP's own buy button. The PDP
    // case is the one that needs the page context — the product being added is
    // the page's own, so it was never in a search or recommendations response.
    const originCard = origin instanceof Element ? origin.closest(PRODUCT_CARD_SELECTOR) : null;
    const ctxProduct = window.__analyticsContext?.product;
    const product =
      getAnalyticsProduct(data.productId) ||
      getAnalyticsProduct(data.handle) ||
      // The card is only trusted when it is demonstrably the product that was
      // added. `origin` falls back to `document.activeElement`, and the cart
      // drawer opens on an add and carries recommendation cards of its own — so
      // without this check a focus change could attribute a neighbouring
      // product's id to the add. Reporting nothing is fine; reporting the wrong
      // product is not.
      (originCard?.dataset?.akeneoId && String(originCard.dataset.productId) === String(data.productId)
        ? buildProductAnalyticsData({
            akeneoProductId: originCard.dataset.akeneoId,
            shopifyProductId: originCard.dataset.productId,
            shopifyProductHandle: originCard.dataset.productHandle,
          })
        : null) ||
      (ctxProduct && String(ctxProduct.id) === String(data.productId)
        ? buildProductAnalyticsData({
            akeneoProductId: ctxProduct.akeneoProductId,
            shopifyProductId: String(ctxProduct.id),
            shopifyProductHandle: ctxProduct.handle,
          })
        : null);
    track(ANALYTICS_EVENTS.ADD_TO_CART, {
      productId: data.productId || '',
      variantId: data.variantId || detail.sourceId || '',
      quantity: data.itemCount || 1,
      cartSource: data.source || '',
      sourcePageType: lastSearch?.context || '',
      sourceQuery: lastSearch?.query || '',
      searchTerm: lastSearch?.query || '',
      analyticsId: resolveAnalyticsId(origin),
      ...(product ? { product } : {}),
    });
  });
}

/* ==================================================================
 * 12. CHECKOUT INITIATED
 * ================================================================*/

/**
 * Tracks clicks on checkout buttons. This is the last event the theme can
 * observe — checkout itself runs no theme JS (see docs/analytics.md).
 */
function trackCheckoutInitiated() {
  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;
    const checkoutBtn = e.target.closest(
      'a[href*="/checkout"], button[name="checkout"], [data-checkout-button], form[action*="/checkout"] button[type="submit"]'
    );
    if (!checkoutBtn) return;

    const ctx = window.__analyticsContext || {};
    const lastSearch = getLastSearch();
    track(ANALYTICS_EVENTS.CHECKOUT_INITIATED, {
      cartItemCount: ctx.cart?.item_count ?? 0,
      cartTotal: ctx.cart?.total_price || '0.00',
      sourcePageType: lastSearch?.context || '',
      sourceQuery: lastSearch?.query || '',
    });
  });
}

/* ==================================================================
 * 13. RECOMMENDATION IMPRESSIONS
 * ================================================================*/

/** Every recommendation surface in the theme, by its root element. */
const REC_SECTION_SELECTOR = [
  'custom-product-recommendations-api', // MLT API section
  '[id^="custom-product-recommendations-"]', // Liquid recommendations section (plain div root)
  'product-recommendations', // Horizon native section
  'cart-drawer-recommendations', // cart drawer recs
  'account-recommendations', // account overview recs
  '[id^="cart-recommended-products-"]', // cart page recs block
].join(', ');

/** @type {WeakSet<Element>} Sections whose impression has already been pushed. */
const _impressedRecSections = new WeakSet();
/** @type {Set<Element>} Observed sections that haven't reported yet. */
const _pendingRecSections = new Set();
/** @type {IntersectionObserver|null} */
let _recObserver = null;

/** Sections currently waiting for their card set to stop changing. */
const _settlingRecSections = new WeakSet();

/** How long a section's card set must hold still before it counts as rendered. */
const REC_SETTLE_MS = 300;

/** Upper bound on that wait, so a section that never settles still reports. */
const REC_SETTLE_MAX_MS = 3000;

/**
 * The distinct product ids currently rendered in a recommendation section.
 *
 * De-duplicated because the card selector matches nested elements — a card can
 * be a `<div data-product-id>` wrapping a `<custom-product-card
 * data-product-id>` wrapping another — so a raw scan counts the same product up
 * to three times. A Set also preserves first-seen order.
 *
 * @param {Element} section
 * @returns {string[]}
 */
function recProductIds(section) {
  const seen = new Set();
  for (const card of section.querySelectorAll(PRODUCT_CARD_SELECTOR)) {
    const productId = card.dataset.ngProdId || card.dataset.productId || card.dataset.akeneoId || '';
    if (productId) seen.add(productId);
  }
  return [...seen];
}

/**
 * Reports a section's impression once its cards have finished arriving.
 *
 * Recommendation cards inject as the API response renders, so the first moment
 * a section becomes visible is usually mid-render — reporting there produced a
 * count that varied with timing (the same page measured 12 products on one load
 * and 4 on the next). This waits for the rendered set to hold still for
 * `REC_SETTLE_MS` before reporting, so the event describes the complete
 * response rather than whatever happened to exist at first sight.
 *
 * Visibility is still what triggers it. An impression that fired on the API
 * response alone would count sections the shopper never scrolled to, which is
 * not an impression.
 *
 * @param {Element} section
 */
function reportImpressionWhenSettled(section) {
  if (_impressedRecSections.has(section) || _settlingRecSections.has(section)) return;
  _settlingRecSections.add(section);

  const startedAt = Date.now();
  let previous = recProductIds(section).join(',');

  const check = () => {
    if (_impressedRecSections.has(section)) {
      _settlingRecSections.delete(section);
      return;
    }

    const productIds = recProductIds(section);
    const current = productIds.join(',');
    const stillChanging = current !== previous;

    if (stillChanging && Date.now() - startedAt < REC_SETTLE_MAX_MS) {
      previous = current;
      setTimeout(check, REC_SETTLE_MS);
      return;
    }

    _settlingRecSections.delete(section);

    // Nothing rendered after all — stay observed and report on a later change.
    if (!productIds.length) return;

    _impressedRecSections.add(section);
    _pendingRecSections.delete(section);
    _recObserver.unobserve(section);
    track(ANALYTICS_EVENTS.RECOMMENDATION_IMPRESSION, {
      productIds,
      // Same per-product shape the search event carries, so one GTM mapping
      // reads both. The cards were registered when the API responded, so the
      // full payload is available here; a card that was rendered some other way
      // still reports its id and position rather than dropping out of the list.
      products: productIds.map((productId, index) => ({
        ...(getAnalyticsProduct(productId) || { prodId: productId }),
        position: index + 1,
      })),
      listName: section.dataset?.sectionId || section.id || section.tagName.toLowerCase(),
      count: productIds.length,
      analyticsId: resolveAnalyticsId(section),
    });
  };

  setTimeout(check, REC_SETTLE_MS);
}

/**
 * Uses IntersectionObserver to track when a recommendation section scrolls
 * into view. Fires a single push per section with all product IDs in it.
 *
 * Cards are injected asynchronously after the API resolves, so a section
 * that intersects while still empty stays observed and reports on a later
 * intersection change once its cards exist. Idempotent — safe to call
 * again whenever new sections may have appeared (drawer open, hydration).
 */
function trackRecommendationImpressions() {
  const sections = document.querySelectorAll(REC_SECTION_SELECTOR);
  if (!sections.length) return;

  if (!_recObserver) {
    _recObserver = new IntersectionObserver(
      function (entries) {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const section = entry.target;
          if (_impressedRecSections.has(section)) continue;

          // Visible — but the response may still be rendering, so hand off to
          // the settle check rather than reporting whatever exists right now.
          reportImpressionWhenSettled(section);
        }
      },
      { threshold: 0.5 }
    );
  }

  for (const section of sections) {
    if (_impressedRecSections.has(section)) continue;
    if (_pendingRecSections.has(section)) {
      // Re-arm: a section that intersected while empty produces no new
      // entry when its cards inject later, so force a fresh evaluation.
      _recObserver.unobserve(section);
      _recObserver.observe(section);
      continue;
    }
    _pendingRecSections.add(section);
    _recObserver.observe(section);
  }
}

/* ==================================================================
 * 14. SHARED ELEMENT HELPERS
 * ================================================================*/

/**
 * GA4 drops event parameters longer than 100 characters, so every free-text
 * param taken from the DOM (link labels, accordion headings, video titles)
 * goes through `textParam` instead of being pushed raw.
 */
const MAX_TEXT_PARAM_LENGTH = 100;

/**
 * Normalises a DOM-derived string into a value GA4 will accept: collapsed
 * whitespace, trimmed, and capped at the parameter length limit.
 *
 * Exported because the source files that own an interaction build their own
 * payloads and need the same normalisation.
 *
 * @param {string|null|undefined} value
 * @returns {string}
 */
export function textParam(value) {
  const text = String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > MAX_TEXT_PARAM_LENGTH ? text.slice(0, MAX_TEXT_PARAM_LENGTH) : text;
}

/**
 * Shopify appends a generated token to the section ids of JSON templates
 * (`template--123__hero_dHNTJj`). The token is noise in a report, so it is
 * stripped — but only when it looks generated (6+ chars, mixed case), so a
 * hand-authored all-lowercase section name like `thin_banner` survives.
 */
const GENERATED_SECTION_SUFFIX = /_(?=[A-Za-z0-9]{6,}$)(?=[A-Za-z0-9]*[a-z])(?=[A-Za-z0-9]*[A-Z])[A-Za-z0-9]+$/;

/**
 * Resolves which Shopify section an element sits in.
 *
 * Shopify wraps every section in `<div id="shopify-section-…">` and that id
 * embeds the section's TYPE (`template--123__hero_dHNTJj` → `hero`). That is
 * the only place the type is readable from the DOM, which is what lets banner
 * and video events classify themselves without every section rendering an
 * extra attribute.
 *
 * @param {Element|null|undefined} el
 * @returns {{ sectionId: string, sectionType: string, analyticsId: string }}
 */
export function resolveSectionContext(el) {
  const analyticsId = resolveAnalyticsId(el);
  const section = el?.closest('[id^="shopify-section-"]');
  if (!section) return { sectionId: '', sectionType: '', analyticsId };

  const sectionId = section.id.slice('shopify-section-'.length);
  // Section ids are `<template-or-group>__<type>`; static sections are just `<type>`.
  const parts = sectionId.split('__');
  const slug = parts.length > 1 ? parts.slice(1).join('__') : sectionId;

  return { sectionId, sectionType: slug.replace(GENERATED_SECTION_SUFFIX, ''), analyticsId };
}

/**
 * Merchant-set Analytics ID of the section an element sits in — the
 * `data-analytics-id` attribute rendered by section-shell.liquid from its
 * "Analytics ID" setting. Lets reports tell apart sections of the same type
 * (e.g. `hp-best-sellers` vs `hp-brand-isotonix`). '' when unset.
 *
 * @param {Element|null|undefined} el
 * @returns {string}
 */
export function resolveAnalyticsId(el) {
  if (!(el instanceof Element)) return '';
  return el.closest('[data-analytics-id]')?.dataset.analyticsId || '';
}

/* ==================================================================
 * 15. BANNER + CTA CLICKS
 * ================================================================*/

/**
 * Interactions that already have their own, more specific event. A click that
 * resolves into one of these is dropped rather than reported a second time as
 * a CTA or banner click — `ng_product_click`, `ng_add_to_cart`,
 * `ng_checkout_initiated`, `ng_navigation_click`, `ng_store_click`, the
 * autocomplete clicks and `ng_expandable_click` all describe the same user
 * action more precisely.
 *
 * `.deferred-media__poster-button` is in the list because the video play
 * button is styled with `.button`: without it, every video play would also
 * report a CTA click.
 */
const TRACKED_ELSEWHERE_SELECTOR = [
  PRODUCT_CARD_SELECTOR,
  '.ng-store-card',
  '[data-volume-id]',
  'li[data-kind]',
  '#header-group',
  '.header',
  'footer',
  '#footer-group',
  '[data-ng-add]',
  '[data-add-to-cart]',
  '.add-to-cart',
  '[name="add"]',
  '[data-ng-subscribe]',
  '[data-subscription]',
  '.subscription-option',
  'a[href*="/checkout"]',
  'button[name="checkout"]',
  '[data-checkout-button]',
  '.deferred-media__poster-button',
  'summary',
].join(', ');

/**
 * Section types whose clicks are banner clicks, matched against the section
 * type from `resolveSectionContext`. Driving this off the section type covers
 * every banner *section* without editing its Liquid; banner *blocks* sit
 * inside a generically-named section and opt in with `data-ga-banner`.
 */
const BANNER_SECTION_PATTERN = /hero|banner|slideshow|slide|promo|carousel/i;

/** Slide containers, so a carousel click can report which slide it came from. */
const SLIDE_SELECTOR = '.swiper-slide, slideshow-slide, [data-slide-index]';

/**
 * Everything the theme styles as a call to action. `.flexible-button` is the
 * flexible-button block's root class and is present for every style preset
 * (including `custom`, which renders no `.button` class), so the block is
 * covered without an opt-in attribute. `[data-ga-cta]` is the hook for
 * naming a specific CTA.
 */
const CTA_SELECTOR = '[data-ga-cta], .button, .button-secondary, .flexible-button';

/**
 * Delegated click handler for banner and CTA clicks.
 *
 * Exactly one event per click, banner winning: a button inside a hero is
 * reported as `ng_banner_click` carrying the banner's identity rather than as
 * a context-free CTA click, so the two events never describe the same
 * interaction twice.
 */
function trackBannerAndCtaClicks() {
  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;

    const trigger = e.target.closest('a[href], button, [role="button"]');
    if (!trigger) return;
    if (trigger.closest(TRACKED_ELSEWHERE_SELECTOR)) return;

    const { sectionId, sectionType, analyticsId } = resolveSectionContext(trigger);
    const linkText = textParam(trigger.textContent || trigger.getAttribute('aria-label'));
    const linkUrl = trigger instanceof HTMLAnchorElement ? trigger.href : '';
    const bannerRoot = trigger.closest('[data-ga-banner]');

    if (bannerRoot || BANNER_SECTION_PATTERN.test(sectionType)) {
      const slide = trigger.closest(SLIDE_SELECTOR);
      track(ANALYTICS_EVENTS.BANNER_CLICK, {
        bannerName: bannerRoot?.dataset.gaBanner || sectionType,
        bannerId: bannerRoot?.id || sectionId,
        sectionId: sectionId,
        sectionType: sectionType,
        analyticsId: analyticsId,
        linkText: linkText,
        linkUrl: linkUrl,
        slidePosition: slide ? resolvePosition(slide, SLIDE_SELECTOR) : 0,
      });
      return;
    }

    const cta = trigger.closest(CTA_SELECTOR);
    if (!cta) return;

    track(ANALYTICS_EVENTS.CTA_CLICK, {
      ctaText: linkText,
      ctaUrl: linkUrl,
      ctaName: cta.dataset?.gaCta || '',
      sectionId: sectionId,
      sectionType: sectionType,
      analyticsId: analyticsId,
    });
  });
}

/* ==================================================================
 * 16. EXPANDABLE ITEM CLICKS
 * ================================================================*/

/**
 * `accordion-custom` is the theme's one accordion element, used for content
 * (PDP details, FAQ rows) AND for chrome: facets, sorting, the mobile menu
 * drawer, footer link groups and the cart discount field. Only content
 * accordions are an "expandable item" worth a report, so the chrome is
 * excluded by its own root selectors.
 */
const EXPANDABLE_EXCLUDE_SELECTOR = [
  '#header-group',
  '.header',
  'footer',
  '#footer-group',
  '.facets__item',
  '[data-filter-param-name]',
  '[id^="Sorting-"]',
  'menu-drawer',
  '.menu-drawer',
  'cart-drawer',
  '.cart-discount',
  'localization-form',
].join(', ');

/**
 * Delegated click handler for expandable items (FAQ rows, PDP accordions).
 *
 * Deliberately a `click` listener on `summary` rather than a `toggle` listener
 * on `details`: `accordion-custom` toggles programmatically — it sets the open
 * state on connect and on every breakpoint change, and closes sibling rows
 * when one opens — so `toggle` would report resizes and side-effects as user
 * interactions.
 *
 * The open/closed state is read BEFORE the browser applies the default toggle
 * (which runs after bubbling listeners), so `details.open` here is the state
 * the click is leaving.
 */
function trackExpandableClicks() {
  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;

    const summary = e.target.closest('summary');
    if (!summary) return;
    if (summary.closest(EXPANDABLE_EXCLUDE_SELECTOR)) return;

    // accordion-custom cancels the toggle at breakpoints where the accordion
    // is disabled — nothing expanded, so nothing to report.
    if (e.defaultPrevented) return;

    const details = summary.closest('details');
    if (!details) return;

    const { sectionId, sectionType, analyticsId } = resolveSectionContext(summary);
    track(ANALYTICS_EVENTS.EXPANDABLE_CLICK, {
      itemLabel: textParam(summary.textContent),
      itemState: details.open ? 'collapsed' : 'expanded',
      itemId: details.id || summary.id || '',
      sectionId: sectionId,
      sectionType: sectionType,
      analyticsId: analyticsId,
    });
  });
}

/* ==================================================================
 * 17. FORM STARTS AND SUBMISSIONS
 * ================================================================*/

/**
 * Inputs that are navigation or display controls rather than a form the
 * visitor is filling in: search, facets, sorting, the country/language picker,
 * variant pickers and cart quantity steppers all live inside `<form>` elements.
 */
const FORM_START_EXCLUDE_SELECTOR = [
  '#header-group',
  '.header',
  'footer',
  '#footer-group',
  'localization-form',
  '.localization-form',
  'quantity-selector',
  'component-cart-quantity-selector',
  'search-modal',
  'predictive-search',
  '[role="search"]',
  '.facets__item',
  '[data-filter-param-name]',
  '[id^="Sorting-"]',
  // Variant pickers wrap their radios in an action-less <form> that is never
  // submitted — selecting a size is a display control, not a form start.
  'swatches-variant-picker-component',
  '.variant-picker__form',
].join(', ');

/**
 * True for the forms that drive the cart rather than collect information: the
 * `{% form 'product' %}` add-to-cart forms (action `/cart/add`) and the cart
 * page's own `{% form 'cart' %}` (action `/cart`).
 *
 * They are excluded from both form events because `ng_add_to_cart` and
 * `ng_checkout_initiated` already report them, with the product and cart
 * context those events carry and these could not.
 *
 * Matched on action rather than by class so it keeps holding for surfaces added
 * later — every one of them is rendered by the same two Liquid form tags.
 *
 * @param {Element} form
 * @returns {boolean}
 */
function isCommerceForm(form) {
  if (form.dataset?.type === 'add-to-cart-form') return true;
  return (form.getAttribute('action') || '').includes('/cart');
}

/** @type {WeakSet<Element>} Forms that have already reported their start. */
const _startedForms = new WeakSet();

/**
 * The identity of a form, shared by `ng_form_start` and `ng_form_submit` so
 * the two join into a funnel in GA4. Both events MUST describe a form the
 * same way — a submit that reports a different `formName` than its own start
 * reads as a submission with no start.
 *
 * @param {Element} form
 * @returns {Object}
 */
function describeForm(form) {
  const { sectionId, sectionType, analyticsId } = resolveSectionContext(form);
  // Shopify's {% form %} tag renders a hidden `form_type` input, which names
  // the platform form (customer, contact, …) more reliably than any class.
  const formType = form.querySelector('[name="form_type"]')?.value || '';

  return {
    formName: form.dataset?.gaForm || form.id || formType || sectionType,
    formType: formType,
    formId: form.id || '',
    fieldCount: form.querySelectorAll('input:not([type="hidden"]), select, textarea').length,
    sectionId: sectionId,
    sectionType: sectionType,
    analyticsId: analyticsId,
  };
}

/**
 * Reports the first interaction with each form on the page (`focusin` on a
 * field), which is the standard "form start" signal — a submit-only event
 * cannot show where visitors abandon.
 *
 * Once per form per page load: `focusin` fires on every tab between fields.
 */
function trackFormStarts() {
  document.addEventListener('focusin', function (e) {
    if (!(e.target instanceof Element)) return;

    const field = e.target.closest('input, select, textarea');
    if (!field) return;
    if (field.type === 'hidden' || field.type === 'search') return;

    const form = field.closest('form, [data-ga-form]');
    if (!form) return;
    if (_startedForms.has(form)) return;
    if (field.closest(FORM_START_EXCLUDE_SELECTOR) || form.closest(FORM_START_EXCLUDE_SELECTOR)) return;
    if (isCommerceForm(form)) return;

    _startedForms.add(form);

    track(ANALYTICS_EVENTS.FORM_START, describeForm(form));
  });
}

/**
 * Reports a form submission that was **accepted** — not one that was merely
 * attempted.
 *
 * This is the deliberate difference from GA4's own enhanced-measurement
 * `form_submit`, which fires on the submit event and therefore counts
 * validation failures, API errors and captcha bounces as submissions. MA's
 * analytics team asked for successful submissions (MA-2674), so the event is
 * emitted from the handler that owns the response — the same rule the request
 * /response events already follow (see docs/analytics.md).
 *
 * Not deduped per page: the contact and catalog-share forms stay usable after
 * a confirmation, so a second accepted submission is a second submission. The
 * native path below is the only one that needs a replay guard.
 *
 * @param {Element} form - The submitted `<form>` (or any `[data-ga-form]` host).
 * @param {Object} [details] - Extra params; `submitMethod` defaults to 'async'.
 */
export function trackFormSubmit(form, details = {}) {
  if (!(form instanceof Element)) return;
  // The same exclusions ng_form_start applies, so the two events always cover
  // the same set of forms. Without this the footer newsletter — excluded from
  // starts by the `footer` rule — would report a submit with no start, the
  // mirror image of the gap this event was added to close.
  if (form.closest(FORM_START_EXCLUDE_SELECTOR) || isCommerceForm(form)) return;

  track(ANALYTICS_EVENTS.FORM_SUBMIT, {
    ...describeForm(form),
    submitMethod: 'async',
    ...details,
  });
}

/**
 * Native `{% form %}` submissions Shopify answers with a redirect back to the
 * page carrying a `*_posted=true` flag. The theme never sees their response,
 * so the flag on the return trip is the success signal.
 *
 * `customer_posted` is deliberately absent: email-signup-form.js already owns
 * that flag (it strips it, and reports the lead), and reports its own submit
 * alongside `ng_email_lead`.
 */
const NATIVE_POSTED_FORMS = [
  { param: 'contact_posted', formType: 'contact' },
  { param: 'comment_posted', formType: 'new_comment' },
];

/**
 * Finds a rendered `{% form %}` by the hidden `form_type` input the tag emits.
 *
 * Matched on that rather than on the action, because `{% form 'customer' %}`
 * (the email signups) posts to `/contact` too — an action match would pick the
 * newsletter form on any page that also holds a contact form.
 *
 * @param {string} formType
 * @returns {Element|null}
 */
function findFormByType(formType) {
  for (const input of document.querySelectorAll('input[name="form_type"]')) {
    if (input.value === formType) return input.closest('form');
  }
  return null;
}

/**
 * Reports native form submissions on the page they redirect back to.
 *
 * The flag is stripped from the address bar afterwards, so reloading a
 * post-submit page is not a second submission — the same guard
 * email-signup-form.js applies to `customer_posted`.
 */
function trackNativeFormSubmits() {
  let params;
  try {
    params = new URL(window.location.href).searchParams;
  } catch {
    return;
  }

  for (const { param, formType } of NATIVE_POSTED_FORMS) {
    if (params.get(param) !== 'true') continue;

    // The form is re-rendered by Liquid on the return trip, so it is still on
    // the page to be described. If it isn't (the flag was pasted into the URL,
    // or the section was removed) there is nothing to report.
    const form = findFormByType(formType);
    if (form) trackFormSubmit(form, { submitMethod: 'native' });

    stripPostedParam(param);
  }
}

/**
 * Removes a `*_posted` flag from the address bar without a navigation.
 * @param {string} param
 */
function stripPostedParam(param) {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete(param);
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // History API unavailable — the cost is a possible duplicate on a manual
    // refresh, which is preferable to throwing here.
  }
}

/**
 * Bridge for submissions reported by scripts that are not ES modules and so
 * cannot import `trackFormSubmit` — today product-reviews.js, which is loaded
 * as a classic `<script>`.
 *
 * Dispatch on success:
 *   document.dispatchEvent(new CustomEvent('ma:form-submitted', { detail: { form } }))
 */
function trackBridgedFormSubmits() {
  document.addEventListener('ma:form-submitted', function (e) {
    const detail = e.detail || {};
    const form = detail.form || (e.target instanceof Element ? e.target.closest('form') : null);
    if (!form) return;

    const extra = { ...detail };
    delete extra.form;
    trackFormSubmit(form, extra);
  });
}

/* ==================================================================
 * 18. VIDEO ENGAGEMENT
 * ================================================================*/

/** Progress milestones to report, ascending. */
const VIDEO_PROGRESS_THRESHOLDS = [25, 50, 75, 90];

/** @type {WeakSet<HTMLVideoElement>} Videos whose start has been reported. */
const _startedVideos = new WeakSet();
/** @type {WeakMap<HTMLVideoElement, Set<number>>} Milestones already reported. */
const _videoMilestones = new WeakMap();

/** @type {WeakMap<HTMLVideoElement, boolean>} Cached `isDecorativeVideo` verdicts. */
const _decorativeVideos = new WeakMap();

/**
 * A poster button is rendered only for videos that do NOT autoplay (every
 * surface gates it behind `unless video_autoplay`: video.liquid, video-card,
 * product-media, pdp-testimonial-card, ugc-product-card). Its presence is
 * therefore the theme's own record that this video is click-to-play.
 */
const VIDEO_POSTER_BUTTON_SELECTOR = '[ref="deferredMediaPlayButton"], .deferred-media__poster-button';

/**
 * True for decorative video — ambient background media that starts itself,
 * stays muted and never ends (`video-background-component`, hero backgrounds,
 * autoplaying video-cards). Reporting it would bury real engagement under
 * page-load noise, so it is filtered out of all three video events.
 *
 * The order of the tests matters. A video sitting behind a poster button only
 * ever plays because someone clicked it, so it counts as engagement whatever
 * its attributes say — `deferred-media` stamps `autoplay` on the element it
 * injects purely to make it start, and a click-to-play thin-banner or
 * video-card can legitimately be muted AND looping. Deciding on
 * `loop && muted && !controls` alone therefore threw away real plays; the
 * autoplay attribute is what actually separates ambient from intentional.
 *
 * Memoised: `timeupdate` fires several times a second for the whole life of a
 * looping video, and this would otherwise walk up the DOM on each one.
 *
 * @param {HTMLVideoElement} video
 * @returns {boolean}
 */
function isDecorativeVideo(video) {
  const cached = _decorativeVideos.get(video);
  if (cached !== undefined) return cached;

  const decorative = (() => {
    if (video.closest('video-background-component, .video-background')) return true;
    if (video.closest('deferred-media')?.querySelector(VIDEO_POSTER_BUTTON_SELECTOR)) return false;
    return video.autoplay && video.muted && video.loop && !video.controls;
  })();

  _decorativeVideos.set(video, decorative);
  return decorative;
}

/**
 * Identity and context parameters shared by all three video events.
 * @param {HTMLVideoElement} video
 * @returns {Object}
 */
function videoParams(video) {
  const source = video.currentSrc || video.src || video.querySelector('source')?.src || '';
  // The CDN URL is far past GA4's 100-character parameter limit; the file name
  // is what identifies the asset in a report.
  const fileName = source.split('?')[0].split('/').pop() || '';
  const { sectionId, sectionType, analyticsId } = resolveSectionContext(video);
  const ctx = window.__analyticsContext || {};

  return {
    videoTitle: textParam(video.getAttribute('title') || video.getAttribute('aria-label') || fileName),
    videoFile: textParam(fileName),
    videoProvider: 'shopify_hosted',
    videoDuration: Number.isFinite(video.duration) ? Math.round(video.duration) : 0,
    videoAutoplay: video.autoplay,
    sectionId: sectionId,
    sectionType: sectionType,
    analyticsId: analyticsId,
    // PDP videos are the priority surface, so tie them to their product.
    productId: ctx.page?.type === 'product' ? ctx.product?.id || '' : '',
  };
}

/**
 * Tracks start / progress / completion for the theme's videos.
 *
 * Listeners are registered on `document` in the CAPTURE phase because media
 * events do not bubble — capture still reaches them on the way down, so one
 * set of listeners covers every `<video>` on the page including those injected
 * later by `deferred-media` when a poster is clicked.
 *
 * Only Shopify-hosted `<video>` is observable this way; YouTube and Vimeo
 * embeds are cross-origin iframes and need their player APIs (see
 * docs/analytics.md).
 */
function trackVideoEngagement() {
  document.addEventListener(
    'play',
    function (e) {
      const video = e.target;
      if (!(video instanceof HTMLVideoElement)) return;
      if (isDecorativeVideo(video)) return;
      // `play` fires again on every resume; a start is once per video per page.
      if (_startedVideos.has(video)) return;

      _startedVideos.add(video);
      track(ANALYTICS_EVENTS.VIDEO_START, videoParams(video));
    },
    true
  );

  document.addEventListener(
    'timeupdate',
    function (e) {
      const video = e.target;
      if (!(video instanceof HTMLVideoElement)) return;
      if (isDecorativeVideo(video)) return;

      const duration = video.duration;
      if (!Number.isFinite(duration) || duration <= 0) return;

      const percent = (video.currentTime / duration) * 100;
      let reached = _videoMilestones.get(video);
      if (!reached) {
        reached = new Set();
        _videoMilestones.set(video, reached);
      }

      for (const threshold of VIDEO_PROGRESS_THRESHOLDS) {
        if (percent < threshold || reached.has(threshold)) continue;
        reached.add(threshold);
        track(ANALYTICS_EVENTS.VIDEO_PROGRESS, {
          ...videoParams(video),
          videoPercent: threshold,
        });
      }
    },
    true
  );

  document.addEventListener(
    'ended',
    function (e) {
      const video = e.target;
      if (!(video instanceof HTMLVideoElement)) return;
      if (isDecorativeVideo(video)) return;

      track(ANALYTICS_EVENTS.VIDEO_COMPLETE, videoParams(video));
    },
    true
  );
}

/* ==================================================================
 * 19. ACCOUNT REGISTRATION (New Customer Accounts)
 * ================================================================*/

/**
 * How long after the customer record was created a page view still counts as
 * "just registered".
 *
 * Registration happens on Shopify's hosted Customer Accounts pages, which run
 * no theme script, so the first page the theme sees is already the return trip
 * — and the Flow workflow that writes the timestamp is asynchronous, so the
 * metafield may not be readable on that very first page view. A window rather
 * than a first-sighting test is what makes both survivable.
 */
const REGISTRATION_WINDOW_MS = 30 * 60 * 1000;

/**
 * When to re-read the timestamp from the server, in ms after the page loads,
 * for a customer this device has just seen sign in for the first time.
 *
 * Flow is asynchronous: the page a customer lands on straight after
 * registering routinely renders an empty timestamp because the workflow has
 * not stamped the metafield yet (verified on dev 2026-09-23). The window above
 * catches that on the NEXT page view — these attempts catch the shopper who
 * never loads one.
 *
 * Three attempts, not a poll: it stops at the first answer, and it stops
 * entirely if the endpoint does not resolve. The spread covers a workflow that
 * lands in a couple of seconds and one that takes most of a minute.
 */
const REGISTRATION_RETRY_SCHEDULE_MS = [8000, 25000, 60000];

/**
 * Alternate cart template rendering the timestamp alone — see
 * templates/cart.registered-at.liquid. Roughly 25 bytes, against the ~300KB a
 * full page would cost to read one attribute.
 */
const REGISTRATION_RETRY_URL = '/cart?view=registered-at';

/**
 * Hard ceiling on re-reads per customer, across every page they load.
 *
 * The budget is what makes re-arming safe. A customer whose account predates
 * the Flow workflow has an empty metafield that will never fill, and without a
 * cap they would re-read on every page view for the whole window.
 */
const REGISTRATION_MAX_ATTEMPTS = 12;

/** Tracks the re-read budget for one customer: `{ since, attempts }`. */
const REGISTRATION_WATCH_KEY = 'ma:registration-watch';


/**
 * Parses the `registered_at` metafield as rendered by meta-tags.liquid.
 *
 * Flow writes ISO 8601 with an extended-form offset
 * (`2026-09-23T13:14:33+00:00`), which every engine parses. The basic form
 * (`+0000`) is normalised anyway because only V8 parses it reliably — Safari's
 * ISO parser wants the colon — and a bare integer is read as Unix seconds, so
 * changing the Liquid to `| date: '%s'` would not silently break this.
 *
 * @param {string} raw
 * @returns {number} Epoch milliseconds, or NaN when unparseable/absent.
 */
function parseRegisteredAt(raw) {
  const value = String(raw || '').trim();
  if (!value) return NaN;
  if (/^\d+$/.test(value)) return Number(value) * 1000;
  return Date.parse(value.replace(/([+-]\d{2})(\d{2})$/, '$1:$2'));
}

/**
 * @type {boolean} Caps reporting at once per page when localStorage is
 * unavailable (private mode, blocked storage), where the marker cannot be
 * written.
 */
let _registrationReported = false;

/**
 * Reports an account registration for a customer whose record was created
 * within the last {@link REGISTRATION_WINDOW_MS}.
 *
 * The timestamp is server-authoritative: a Shopify Flow workflow stamps
 * `customer.metafields.custom.registered_at` on the "Customer created"
 * trigger, and meta-tags.liquid renders it onto the `ma:customer` meta tag.
 * That is what separates this from the localStorage first-sighting proxy
 * docs/analytics.md rejected — an existing customer on a new device has an old
 * timestamp and is never counted.
 *
 * Fires at most once per customer per device: the window bounds it, and a
 * localStorage marker keyed on the customer id stops a second page view inside
 * the window reporting again.
 */
function trackAccountRegistration() {
  const meta = document.querySelector('meta[name="ma:customer"]');
  if (meta?.dataset.loggedIn !== 'true') return;

  const customerId = meta.dataset.customerId || '';
  if (!customerId) return;

  // Anything other than "no timestamp at all" is a settled answer: it either
  // reported, or the account is too old to ever qualify. Only an absent value
  // is worth asking the server about again.
  if (reportRegistration(customerId, meta.dataset.registeredAt) !== 'absent') return;

  // Still nothing. Re-read on a short schedule — and re-arm on the NEXT page
  // too, rather than spending a one-shot gate here. The pending timers below
  // die the moment the shopper navigates, so a single page's worth of attempts
  // is lost to any click; and Flow can take longer than the schedule anyway.
  // The budget is what keeps that bounded.
  if (!claimRetryBudget(customerId)) return;

  retryAccountRegistration(customerId);
}

/**
 * Reports the registration if the timestamp says it just happened.
 *
 * Split out from the page-load check so the retry path below reaches exactly
 * the same window test, marker and payload — a second copy of this logic would
 * be a second place for the fire-once guarantee to drift.
 *
 * @param {string} customerId
 * @param {string} raw - Timestamp as rendered by meta-tags.liquid, or fetched.
 * @returns {'reported'|'already-reported'|'outside-window'|'absent'}
 */
function reportRegistration(customerId, raw) {
  if (_registrationReported) return 'already-reported';

  const registeredAtMs = parseRegisteredAt(raw);
  if (!Number.isFinite(registeredAtMs)) return 'absent';

  const age = Date.now() - registeredAtMs;
  // A negative age is a clock skew between the shop and the browser, not a
  // registration in the future.
  if (age < 0 || age > REGISTRATION_WINDOW_MS) return 'outside-window';

  const markerKey = `ma:account-registration:${customerId}`;
  try {
    if (window.localStorage.getItem(markerKey)) return 'already-reported';
  } catch {
    // Storage unavailable — the in-memory flag below still caps this at one
    // event per page view.
  }

  _registrationReported = true;

  try {
    window.localStorage.setItem(markerKey, String(Date.now()));
  } catch {
    // See above.
  }

  track(ANALYTICS_EVENTS.ACCOUNT_REGISTRATION, {
    registrationMethod: 'customer_account',
    registrationSource: 'SHPFYMA',
    registeredAt: Math.round(registeredAtMs / 1000),
  });

  return 'reported';
}

/**
 * Whether this page load may spend re-reads on this customer, and records that
 * it did.
 *
 * Three things bound it, so re-arming on every page view cannot run away:
 * the 30-minute window (a watch older than that can never report, so it stops),
 * {@link REGISTRATION_MAX_ATTEMPTS}, and the fire-once marker that
 * `reportRegistration` checks before this is ever reached.
 *
 * Returns false when storage is unavailable: without somewhere to keep the
 * budget the re-reads could not be bounded at all, and those shoppers are still
 * covered by the window on their next page view.
 *
 * @param {string} customerId
 * @returns {boolean}
 */
function claimRetryBudget(customerId) {
  const key = `${REGISTRATION_WATCH_KEY}:${customerId}`;

  try {
    const now = Date.now();
    let watch;

    try {
      watch = JSON.parse(window.localStorage.getItem(key) || 'null');
    } catch {
      watch = null;
    }

    // First sighting of this customer on this device: open a watch. An
    // existing customer signing in here opens one too, and closes it again on
    // the first read — their timestamp is old, which is a settled answer.
    if (!watch || typeof watch.since !== 'number') {
      watch = { since: now, attempts: 0 };
    }

    if (now - watch.since > REGISTRATION_WINDOW_MS) return false;
    if (watch.attempts >= REGISTRATION_MAX_ATTEMPTS) return false;

    window.localStorage.setItem(key, JSON.stringify(watch));
    return true;
  } catch {
    return false;
  }
}

/**
 * Charges one re-read against the customer's budget.
 * @param {string} customerId
 */
function spendRetryAttempt(customerId) {
  const key = `${REGISTRATION_WATCH_KEY}:${customerId}`;
  try {
    const watch = JSON.parse(window.localStorage.getItem(key) || 'null');
    if (!watch) return;
    watch.attempts = (watch.attempts || 0) + 1;
    window.localStorage.setItem(key, JSON.stringify(watch));
  } catch {
    // Storage went away mid-visit; the schedule below is finite regardless.
  }
}

/**
 * Re-reads the timestamp from the server on a short schedule, for a customer
 * who has just signed in on this device and whose metafield was still empty
 * when the page rendered.
 *
 * @param {string} customerId
 */
async function retryAccountRegistration(customerId) {
  let elapsed = 0;

  for (const at of REGISTRATION_RETRY_SCHEDULE_MS) {
    await new Promise((resolve) => setTimeout(resolve, at - elapsed));
    elapsed = at;

    spendRetryAttempt(customerId);

    const raw = await readRegisteredAt();
    // null is the endpoint failing rather than the value being absent —
    // retrying a template that isn't there just repeats the same miss.
    if (raw === null) return;
    if (reportRegistration(customerId, raw) !== 'absent') return;
  }
}


/**
 * Fetches the timestamp from the alternate cart template.
 *
 * @returns {Promise<string|null>} The raw value ('' when Flow has not stamped
 *   it yet), or null when the endpoint did not answer usefully.
 */
async function readRegisteredAt() {
  try {
    const response = await fetch(REGISTRATION_RETRY_URL, { credentials: 'same-origin' });
    if (!response.ok) return null;

    const body = (await response.text()).trim();
    // With the template missing, Shopify serves the ordinary cart page and the
    // `view` param is ignored. Length is what tells the two apart: the template
    // renders a timestamp and nothing else, so anything longer is a full page
    // and must not be parsed as a date.
    if (body.length > 64) return null;

    return body;
  } catch {
    // Offline, navigated away, blocked by an extension — the window still
    // covers the next page view.
    return null;
  }
}

/* ==================================================================
 * INITIALISATION
 * ================================================================*/

/**
 * Boots the page-level and delegated tracking owned by this module. Runs
 * once — ES modules execute a single time per URL regardless of how many
 * importers there are.
 */
function initAnalytics() {
  // Bail if the Liquid context snippet wasn't rendered (GTM not configured).
  if (!window.__analyticsContext) return;

  trackPageView();
  trackCollectionView();

  trackProductClicks();
  trackStoreClicks();
  trackStoreLinkOff();
  trackAutocompleteClicks();
  trackNavigationClicks();
  trackAddToCart();
  trackCheckoutInitiated();
  trackBannerAndCtaClicks();
  trackExpandableClicks();
  trackFormStarts();
  trackNativeFormSubmits();
  trackBridgedFormSubmits();
  trackVideoEngagement();
  trackAccountRegistration();

  // Deferred: wait for recommendation sections to render.
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(trackRecommendationImpressions);
  } else {
    setTimeout(trackRecommendationImpressions, 2000);
  }

  // Cart changes re-render drawer recommendations.
  document.addEventListener('cart:update', function () {
    setTimeout(trackRecommendationImpressions, 500);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAnalytics);
} else {
  initAnalytics();
}
